import { blockLabel } from "../domain/labels";
import type { Block, EmailDocument } from "../domain/types";
import { isGroup, isRow } from "../domain/sections";
import { SOCIAL_LABELS } from "../domain/media";
import type { TEmailBuilderValidationMessageDescriptor } from "./translateEmailBuilderValidationMessage";

const BLOCK_PATH_PATTERN =
  /^sections\[(\d+)\](?:\.rows\[(\d+)\])?\.columns\[(\d+)\]\.blocks\[(\d+)\](?:\.links\[(\d+)\])?(?:\.(\w+))?/;

type TResolvedValidationBlockContext = {
  block: Block;
  fieldLabel: string | TEmailBuilderValidationMessageDescriptor;
};

function resolveSocialLinkFieldLabel(
  block: Block,
  linkIndex: number,
): string | TEmailBuilderValidationMessageDescriptor {
  if (block.type !== "social") {
    return "Link URL";
  }

  const link = block.links[linkIndex];
  if (link === undefined) {
    return "Social link URL";
  }

  if (link.network === "custom") {
    const title = link.title?.trim();
    if (title && title.length > 0) {
      return {
        key: "{{title}} URL",
        literalValues: { title },
      };
    }
    return "Custom social link URL";
  }

  return {
    key: "{{network}} URL",
    values: { network: SOCIAL_LABELS[link.network] },
  };
}

function resolveBlockFromValidationPath(
  document: EmailDocument | null,
  apiMessage: string,
): TResolvedValidationBlockContext | null {
  if (document === null) {
    return null;
  }

  const match = apiMessage.match(BLOCK_PATH_PATTERN);
  if (match === null) {
    return null;
  }

  const sectionIndex = Number.parseInt(match[1], 10);
  const rowIndex = match[2] !== undefined ? Number.parseInt(match[2], 10) : null;
  const columnIndex = Number.parseInt(match[3], 10);
  const blockIndex = Number.parseInt(match[4], 10);
  const linkIndex = match[5] !== undefined ? Number.parseInt(match[5], 10) : null;
  const fieldKey = match[6] ?? null;

  const section = document.sections[sectionIndex];
  if (section === undefined) {
    return null;
  }

  let row: { columns: Array<{ blocks: Block[] }> } | undefined;
  if (isGroup(section)) {
    const groupRow = rowIndex !== null ? section.rows[rowIndex] : section.rows[0];
    row = groupRow;
  } else if (isRow(section)) {
    row = section;
  }

  if (row === undefined) {
    return null;
  }

  const column = row.columns[columnIndex];
  if (column === undefined) {
    return null;
  }

  const block = column.blocks[blockIndex];
  if (block === undefined) {
    return null;
  }

  let fieldLabel: string | TEmailBuilderValidationMessageDescriptor =
    fieldKey === "href" || fieldKey === "url" ? "Link URL" : fieldKey ?? "field";
  if (block.type === "social" && linkIndex !== null) {
    fieldLabel = resolveSocialLinkFieldLabel(block, linkIndex);
  }

  return { block, fieldLabel };
}

function withBlockContext(
  document: EmailDocument | null,
  apiMessage: string,
  detailKey: string,
): TEmailBuilderValidationMessageDescriptor | string {
  const context = resolveBlockFromValidationPath(document, apiMessage);
  if (context === null) {
    return detailKey;
  }

  return {
    key: "{{block}}: {{field}} — {{detail}}",
    values: {
      block: blockLabel(context.block),
      field: context.fieldLabel,
      detail: detailKey,
    },
  };
}

function isTechnicalValidationMessage(message: string): boolean {
  return (
    message.includes("sections[") ||
    message.startsWith("settings.") ||
    message.startsWith("schemaVersion")
  );
}

export function mapEmailBuilderValidationError(
  apiMessage: string,
  document: EmailDocument | null,
): TEmailBuilderValidationMessageDescriptor | string {
  const trimmed = apiMessage.trim();
  if (trimmed.length === 0) {
    return "We couldn't save your design. Check links, images, and colors, then try again.";
  }

  if (!isTechnicalValidationMessage(trimmed)) {
    return trimmed;
  }

  if (
    trimmed.includes(".url must be an absolute URL with an allowed scheme.") ||
    trimmed.includes(".url scheme '") ||
    trimmed.includes(".url must include a host.")
  ) {
    return withBlockContext(
      document,
      trimmed,
      "Enter a valid web address (e.g. example.com or https://…).",
    );
  }

  if (
    trimmed.includes(".href must be an absolute URL with an allowed scheme.") ||
    trimmed.includes(".href scheme '") ||
    trimmed.includes(".href must include a host.")
  ) {
    return withBlockContext(
      document,
      trimmed,
      "Enter a valid URL (e.g. example.com or https://…) or merge tag.",
    );
  }

  if (trimmed.includes(" must be a #RGB or #RRGGBB color.")) {
    return withBlockContext(document, trimmed, "Enter a valid color (#RGB or #RRGGBB).");
  }

  if (trimmed.includes(" must be a hex color string.")) {
    return withBlockContext(document, trimmed, "Enter a valid color.");
  }

  if (trimmed.includes(" exceeds ")) {
    return withBlockContext(document, trimmed, "This text is too long.");
  }

  if (trimmed.includes(" contains unsupported markup.")) {
    return withBlockContext(document, trimmed, "This content uses unsupported formatting.");
  }

  if (trimmed.includes(" contains unsupported merge tag")) {
    return withBlockContext(document, trimmed, "This field uses an unsupported merge tag.");
  }

  if (trimmed.startsWith("Unsupported schemaVersion")) {
    return "This design uses an outdated format and can't be saved.";
  }

  if (trimmed.includes("Email builder media") && trimmed.includes("is not available")) {
    return "An image in this design is no longer available. Replace it and try again.";
  }

  return "We couldn't save your design. Check links, images, and colors, then try again.";
}
